import { type ReactNode, useId, useRef } from 'react';

import { Button } from '../button/Button';
import styles from './ConfirmDialog.module.css';
import { Dialog } from './Dialog';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  className?: string;
  'data-testid'?: string;
}

export const ConfirmDialog = ({
  open,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  onConfirm,
  onCancel,
  className,
  'data-testid': testId = 'confirm-dialog',
}: ConfirmDialogProps) => {
  const descriptionId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      role="alertdialog"
      aria-describedby={children ? descriptionId : undefined}
      title={title}
      size="sm"
      closeButton={false}
      initialFocusRef={cancelRef}
      className={className}
      data-testid={testId}
      footer={
        <>
          <Button
            ref={cancelRef}
            variant="link"
            size="sm"
            className={styles.action}
            onClick={onCancel}
            disabled={loading}
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </Button>
          <Button
            color={destructive ? 'destructive' : 'primary'}
            size="sm"
            className={styles.action}
            onClick={onConfirm}
            loading={loading}
            data-testid={`${testId}-confirm`}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children ? <div id={descriptionId}>{children}</div> : null}
    </Dialog>
  );
};
